import { contactDisplayName, contactInitials } from '@taki/data-model';
import type { Contact } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';
import type { Cell, Column, ViewBinding, ViewType } from '@taki/ui';
import { calendrier, fiche, kanban, liste, tableau } from '@taki/ui/views/core';

/**
 * LE CATALOGUE DE VUES ET LES BINDINGS de l'ecran Contacts, partages entre
 * l'ecran et le module de reglages. Un seul endroit : sinon la liste deroulante
 * des Parametres et le selecteur de la zone 3c finissent par ne plus proposer
 * la meme chose, et le reglage "vue par defaut" pourrait epingler une vue que
 * l'ecran grise (ViewDefaultSetting lit les MEMES bindings pour griser les
 * memes entrees, avec la meme raison).
 *
 * Les vues sont importees EXPLICITEMENT depuis le sous-chemin
 * `@taki/ui/views/core` : une vue ne s'auto-enregistre jamais, sans cet import
 * le build la supprimerait.
 *
 * Kanban et calendrier sont proposes MAIS GRISES, et c'est le comportement
 * VOULU : un contact n'a ni champ de regroupement ni date metier. Une vue
 * impossible reste visible avec sa raison au survol, sinon "pourquoi mon kanban
 * n'est pas propose" devient un bug silencieux.
 */
export const CONTACT_VIEW_TYPES: ViewType[] = [liste, tableau, kanban, calendrier, fiche];

/** Ids passes a `useScreen` et a `ViewDefaultSetting` : ils doivent coincider. */
export const CONTACT_APP_ID = 'contact';
export const CONTACT_SCREEN_ID = 'contacts';

/* ------------------------------------------------------------------ */
/* Ce que SONT les donnees : les bindings                              */
/* ------------------------------------------------------------------ */

/**
 * Le nom affiche : role `title` de toutes les vues. C'est le helper du SOCLE
 * qui decide (raison sociale d'une organisation, prenom-nom d'une personne,
 * replis) : une concatenation locale `firstName + lastName` afficherait une
 * organisation vide. Meme source pour les initiales de l'avatar.
 */
const fullName = contactDisplayName;

/** Coordonnees en une ligne : role `subtitle`. Vide, le texte `empty` prend le relais. */
function coordinates(contact: Contact): string {
  return [contact.email, contact.phone].filter(Boolean).join(' · ');
}

/**
 * Les colonnes du tableau. `value` sert au tri, a l'export et aux agregats :
 * une valeur absente vaut `null` (elle finit en bas du tri, quel que soit le
 * sens), jamais `undefined`.
 */
const CONTACT_COLUMNS: Column<Contact>[] = [
  { key: 'email', label: 'Email', value: (contact) => contact.email ?? null, sortable: true },
  { key: 'phone', label: 'Telephone', value: (contact) => contact.phone ?? null, sortable: true },
  { key: 'createdAt', label: 'Cree le', value: (contact) => contact.createdAt, sortable: true },
];

/**
 * Les colonnes de la FICHE : les memes que le tableau, ENRICHIES du prenom et
 * du nom, et EDITABLES. Deux raisons de ne pas reutiliser telles quelles celles
 * du tableau :
 *
 * - le titre de la fiche est le nom COMPLET, une concatenation. On n'edite pas
 *   une concatenation : ce sont `firstName` et `lastName` qui portent la donnee,
 *   et ils n'ont pas leur place dans le tableau, ou la colonne "Nom" les montre
 *   deja assembles ;
 * - `createdAt` est un champ STANDARD, rempli par le SDK. Il reste affiche mais
 *   sans `set()` : la fiche ne rend donc aucun editeur dessus.
 *
 * Ces colonnes ont besoin du SDK pour ecrire : elles se fabriquent par appel,
 * une seule fois, dans le `useMemo` de l'ecran.
 */
function createContactFicheColumns(sdk: TakiSdk): Column<Contact>[] {
  /* Un champ NATIF non vide : le contact doit rester identifiable dans toutes
     les vues. Une case videe est refusee, la valeur revient et l'echec
     s'affiche dans la rangee 3 (indicateur d'enregistrement), plutot que
     d'ignorer la saisie en silence. */
  const required =
    (key: 'firstName' | 'lastName', label: string) =>
    async (contact: Contact, value: Cell): Promise<void> => {
      const next = typeof value === 'string' ? value.trim() : '';
      if (next === '') throw new Error(`${label} ne peut pas etre efface, seulement corrige.`);
      await sdk.contacts.update(contact.id, { [key]: next });
    };

  /*
   * Email et telephone sont OPTIONNELS dans le modele, et pourtant une case
   * videe est REFUSEE ici. Ce n'est pas un choix de produit, c'est une limite
   * du socle qu'il ne faut pas contourner en douce :
   *
   * - leur schema est `.optional()`, donc `undefined`, PAS `.nullable()` ;
   * - `buildUpdateData` ne traduit `null` en `deleteField()` que pour les cles
   *   de `customFields`. Sur un champ NATIF, `null` s'ecrirait litteralement,
   *   et la relecture (qui parse avec Zod) refuserait alors le document ;
   * - `undefined` est ignore par le patch : la saisie serait perdue en silence,
   *   ce qui est pire que le refus.
   *
   * Effacer un champ natif optionnel demande donc une capacite du SDK qui
   * n'existe pas encore. En attendant, on corrige, on ne vide pas, et on le
   * DIT : la valeur revient et l'echec s'affiche dans la rangee 3.
   */
  const correctable =
    (key: 'email' | 'phone' | 'name', label: string) =>
    async (contact: Contact, value: Cell): Promise<void> => {
      const next = typeof value === 'string' ? value.trim() : '';
      if (next === '') {
        throw new Error(
          `${label} ne peut pas etre efface depuis la fiche, seulement corrige (limite du SDK).`,
        );
      }
      await sdk.contacts.update(contact.id, { [key]: next });
    };

  return [
    {
      key: 'firstName',
      label: 'Prenom',
      value: (contact) => contact.firstName ?? null,
      set: required('firstName', 'Le prenom'),
    },
    {
      key: 'lastName',
      label: 'Nom',
      value: (contact) => contact.lastName ?? null,
      set: required('lastName', 'Le nom'),
    },
    {
      /* La raison sociale d'une ORGANISATION. Sur une personne la case reste
         vide et sert de repli d'affichage si elle est remplie. Meme limite du
         SDK que l'email : on corrige, on ne vide pas. */
      key: 'name',
      label: 'Raison sociale',
      value: (contact) => contact.name ?? null,
      set: correctable('name', 'La raison sociale'),
    },
    {
      key: 'email',
      label: 'Email',
      value: (contact) => contact.email ?? null,
      sortable: true,
      set: correctable('email', "L'email"),
    },
    {
      key: 'phone',
      label: 'Telephone',
      value: (contact) => contact.phone ?? null,
      sortable: true,
      set: correctable('phone', 'Le telephone'),
    },
    {
      key: 'createdAt',
      label: 'Cree le',
      value: (contact) => contact.createdAt,
      sortable: true,
    },
  ];
}

/**
 * UN BINDING PAR VUE : la meme donnee ne se raconte pas pareil en liste et en
 * tableau.
 *
 * Ces bindings ont besoin du SDK depuis que la fiche s'edite en place : ils ne
 * peuvent donc pas etre une constante de module. Ils se fabriquent ICI, par une
 * fonction que l'ecran ET le module de reglages appellent UNE fois chacun, dans
 * un `useMemo` cle sur le SDK (stable pour toute la vie de l'ecran). Le
 * resultat est donc aussi stable qu'une constante : la projection ne se
 * recalcule que quand les donnees ou la requete changent.
 */
export function createContactBindings(sdk: TakiSdk): ViewBinding<Contact>[] {
  return [
    {
      /* LISTE : exactement ce que le <ul>/<li> fait main affichait. */
      type: 'core.liste',
      roles: {
        title: { get: fullName },
        subtitle: { get: coordinates, empty: 'Aucune coordonnee' },
        avatar: { get: contactInitials },
      },
    },
    {
      /* TABLEAU : comparer. Sans colonne declaree, l'entree serait grisee. */
      type: 'core.tableau',
      roles: {
        title: { get: fullName },
        avatar: { get: contactInitials },
      },
      columns: CONTACT_COLUMNS,
      // Sac libre, lu par ce SEUL type de vue : ici le titre EST un nom.
      options: { titleLabel: 'Nom' },
    },
    {
      /* FICHE : consulter ET MODIFIER un contact, avec "n / total" et les
         fleches. Le titre reste en lecture seule : c'est le nom COMPLET, une
         concatenation, et on n'edite pas une concatenation. Ce sont les colonnes
         "Prenom" et "Nom" de la grille qui portent la donnee reelle. */
      type: 'core.fiche',
      roles: {
        title: { get: fullName },
        subtitle: { get: coordinates, empty: 'Aucune coordonnee' },
        avatar: { get: contactInitials },
      },
      columns: createContactFicheColumns(sdk),
    },
    {
      /*
       * KANBAN : DELIBEREMENT INCOMPLET. Un contact n'a aucun champ d'etat : ni
       * statut, ni etape, ni pipeline. On ne fabrique pas un `groupKey` pour
       * faire joli (ce serait une donnee inventee), donc l'entree reste grisee
       * avec sa raison au survol : "Role manquant : groupKey".
       *
       * Le jour ou l'app gagnera un vrai champ d'etat, il suffira d'ajouter
       * `groupKey` (avec son `options()` et, pour deplacer les cartes, son
       * `set()`) ICI : aucune ligne de rendu a ecrire.
       */
      type: 'core.kanban',
      roles: {
        title: { get: fullName },
        subtitle: { get: coordinates, empty: 'Aucune coordonnee' },
      },
    },
    {
      /*
       * CALENDRIER : DELIBEREMENT INCOMPLET, meme raison. Un contact n'a pas de
       * date METIER : `createdAt` est un champ technique du socle, le poser en
       * `dateStart` remplirait un calendrier de faux rendez-vous. L'entree reste
       * donc grisee : "Role manquant : dateStart".
       */
      type: 'core.calendrier',
      roles: {
        title: { get: fullName },
        subtitle: { get: coordinates, empty: 'Aucune coordonnee' },
      },
    },
  ];
}
